import React, { useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { format } from "date-fns";
import { MarkdownRenderer } from "@/components/feature/markdown/markdownRenderer";
import { type Message } from "@/components/feature/layout/types";
import { AssistantMessageCopy } from "@/views/chat/components/assistantMessageCopy";

interface AssistantMessageProps {
  message: Message;
  isStreaming: boolean;
}

const TypingIndicator: React.FC = () => (
  <motion.div
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.8 }}
    transition={{ duration: 0.3 }}
    className="ml-1 inline-flex space-x-1 align-middle"
  >
    {[0, 1, 2].map(dot => (
      <motion.div
        key={dot}
        className="h-1.5 w-1.5 rounded-full bg-brand-gold-500"
        animate={{ scale: [1, 1.2, 1] }}
        transition={{
          duration: 0.6,
          repeat: Infinity,
          repeatType: "reverse",
          delay: dot * 0.2,
        }}
      />
    ))}
  </motion.div>
);

export const AssistantMessage: React.FC<AssistantMessageProps> = ({ message, isStreaming }) => {
  const formattedTime = useMemo(() => format(new Date(), "h:m a"), []);

  return (
    <motion.div
      className="my-4 flex justify-start"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ type: "spring", stiffness: 500, damping: 30, mass: 1 }}
    >
      <div className="flex w-full max-w-[95%] flex-col">
        <motion.div
          className="relative rounded-lg border border-gray-200 bg-assistant-message p-4 shadow-md"
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.3 }}
        >
          <div className="mb-3 flex items-center border-b border-gray-200 pb-2">
            <span className="mr-2 text-sm font-semibold">Pathfinder</span>
            <span className="ml-auto text-xs text-gray-500">{formattedTime}</span>
          </div>
          <div className="pt-1">
            <MarkdownRenderer content={message.content} evidences={message.evidences} processCitations={true} />
            <AnimatePresence>{isStreaming && <TypingIndicator />}</AnimatePresence>
          </div>
        </motion.div>
        <AssistantMessageCopy message={message} />
      </div>
    </motion.div>
  );
};
